import { useMutation } from '@apollo/client';
import { BaseText, Button, Meta, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import MenuItem from 'components/common/MenuItem';
import Icon from 'components/icons/Icon';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CenterVerticalRow,
  ColumnWithMargins,
  StyledWrapper,
} from 'styles/ContainerStyles';

import { CREATE_PARKING_SESSION } from 'graphql/mutations/createParkingSession';
import { READ_PARKING_SESSIONS } from 'graphql/queries/readParkingSessions';

import useSelectedCompanyBenefit from 'hooks/useSelectedCompanyBenefit';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import useSessionPaymentState from 'hooks/useSessionPaymentState';

import { useNavigation } from '@react-navigation/core';
import Validity from 'components/permit/Validity';
import useCache from 'hooks/useCache';
import useCreateExternalPayment from 'hooks/useCreateExternalPayment';
import { createPrepaidInput } from 'hooks/useReadPurchasedPrepaidTickets';
import { latestVehicleUsed } from 'states/persistInStorage';
import { useParkingStore } from 'stores/parkingStore';
import { usePaymentStore } from 'stores/paymentStore';
import { usePrepaidTicketStore } from 'stores/prepaidTicketStore';
import {
  Mutation,
  MutationCreateParkingSessionArgs,
  ParkingSession,
  ParkingSessions,
  ParkingSessionType,
  Query,
} from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { currencyFormatter } from 'utils/commonUtils';
import { evictFromCache } from 'utils/cacheUtils';
import { isWeb } from 'utils/deviceUtils';
import { updateStateAndStorage } from 'utils/MMKVStorageUtils';
import {
  determinePaymentType,
  getCAbenefit,
  getWalletCardMenuPresentation,
  isCard,
  isCorporateAccountPaymentOption,
  isExternalPayment,
} from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const PrepaidTicketConfirmModalContent: React.FC = () => {
  const prepaidPurchaseData = usePrepaidTicketStore(
    (state) => state.confirmPrepaidTicketData
  );
  const { goBack } = useNavigation<ParkNavProps>();
  const paymentMethod = useSelectedPaymentMethod();
  const { apolloCache } = useCache();

  const removeParkingSessionByEviction = useCallback(
    (sessionId?: string | null) => {
      if (!sessionId) {
        return;
      }
      evictFromCache(apolloCache, {
        __typename: 'ParkingSession',
        sessionId,
      });
    },
    [apolloCache]
  );
  const { pending: isPaymentPending, paymentState } = useSessionPaymentState();
  const { t } = useTranslation();

  const handlePrepaidSuccess = useCallback(async () => {
    showToast(
      `${t('success.prepaidTicketPurchase', { zone: prepaidPurchaseData?.parkzoneLocation })}`,
      'success'
    );
    updateStateAndStorage(
      latestVehicleUsed,
      'latestVehicleUsed',
      prepaidPurchaseData?.licensePlate
    );
    useParkingStore.getState().setCurrentParkingSession(null);
    useUiStore.getState().setSheetModal('');
    goBack();
  }, [
    goBack,
    prepaidPurchaseData?.licensePlate,
    prepaidPurchaseData?.parkzoneLocation,
    t,
  ]);

  const { createExternalPayment } = useCreateExternalPayment({
    onFreeComplete: handlePrepaidSuccess,
  });
  const corporateAccountTransactionFee =
    useSelectedCompanyBenefit()?.corporateAccountTransactionFee;

  const isCreditCard = isCard(paymentMethod);
  const walletCard = getWalletCardMenuPresentation(paymentMethod, t);

  const effectiveServiceFee = isCorporateAccountPaymentOption(paymentMethod)
    ? corporateAccountTransactionFee
    : prepaidPurchaseData?.serviceFee;

  const priceText =
    prepaidPurchaseData?.price != null
      ? currencyFormatter(
          prepaidPurchaseData.price,
          prepaidPurchaseData.countryCode ?? 'FI'
        )
      : undefined;

  const serviceFeeText =
    effectiveServiceFee && effectiveServiceFee > 0
      ? t('price.serviceFee', {
          price: currencyFormatter(
            effectiveServiceFee,
            prepaidPurchaseData?.countryCode ?? 'FI'
          ),
        })
      : undefined;

  const getPaymentMethodDisplayName = () => {
    if (!paymentMethod) {
      return '';
    }

    if (walletCard) {
      return walletCard.subtitle;
    }

    if (paymentMethod.cardId) {
      return `${t('payment.creditCardOnly')}**** ${paymentMethod.lastNums}`;
    }

    if (isCorporateAccountPaymentOption(paymentMethod)) {
      return (paymentMethod as any).optionName;
    }

    if (paymentMethod.method === 'MOBILEPAY') {
      return t('paymentMethods.mobilePay');
    }

    return paymentMethod.name || '';
  };

  const [createParking, { loading: createParkingLoading }] = useMutation<
    Mutation,
    MutationCreateParkingSessionArgs
  >(CREATE_PARKING_SESSION, {
    variables: {
      input: {
        type: prepaidPurchaseData?.type as ParkingSessionType,
        cardId: isCreditCard ? (paymentMethod?.cardId as string) : null,
        paymentType: determinePaymentType(paymentMethod),
        corporateAccountUid: getCAbenefit(paymentMethod),
        customerEmail: prepaidPurchaseData?.customerEmail || '',
        customerSegment: prepaidPurchaseData?.customerSegment,
        predefinedTicketUid: prepaidPurchaseData?.predefinedTicketUid || '',
        startDate: prepaidPurchaseData?.validFrom,
        licensePlate: prepaidPurchaseData?.licensePlate,
        parkingZoneUid: prepaidPurchaseData?.parkingZoneUid || '',
        serviceFeeAmount: corporateAccountTransactionFee,
      },
    },
    onCompleted: (data) => {
      const sessionId = data?.createParkingSession?.sessionId || '';
      useParkingStore.getState().setCurrentParkingSession(sessionId);
      if (isExternalPayment(paymentMethod)) {
        usePaymentStore.getState().setExternalPaymentFlowLoading(true);
        createExternalPayment({
          variables: {
            input: {
              actionType: 'START_PARKING',
              countryCode: prepaidPurchaseData?.countryCode,
              paymentMethod: paymentMethod?.externalPaymentType,
              items: [
                {
                  parkingSessionId: sessionId,
                  licensePlate: prepaidPurchaseData?.licensePlate,
                  type: prepaidPurchaseData?.type as ParkingSessionType,
                },
              ],
            },
          },
        });
      }
    },
    onError: (error) => {
      if (
        error.graphQLErrors.some(
          (e) => e.extensions?.code === 'PREPAID_TICKET_DUPLICATED'
        )
      ) {
        showToast(
          `${t('parking.prepaidTickets.hasValidTicket', {
            zone: prepaidPurchaseData?.parkzoneLocation,
            vehicle: prepaidPurchaseData?.licensePlate,
          })}`,
          'info'
        );
        return;
      }
      showToast('error.parkingStartedFailed', 'error');
    },
    update: (cache, { data: createData }) => {
      cache?.updateQuery(
        {
          query: READ_PARKING_SESSIONS,
          variables: createPrepaidInput({
            fetchAll: false,
            commercialZoneCode: prepaidPurchaseData?.zoneCode,
            country: prepaidPurchaseData?.countryCode,
          }),
          overwrite: true,
        },
        (data: Query | null) => {
          return {
            readParkingSessions: {
              ...data?.readParkingSessions,
              content: [
                ...(data?.readParkingSessions?.content ?? []),
                createData?.createParkingSession as ParkingSession,
              ] as ParkingSession[],
            } as ParkingSessions,
          };
        }
      );
    },
  });

  useEffect(() => {
    const sessionId = useParkingStore.getState().currentParkingSession;
    switch (paymentState) {
      case 'Success':
      case 'Billing':
        handlePrepaidSuccess();
        break;
      case 'Terminated':
      case 'Failed':
        showToast('parking.receipt.paymentFailed', 'error');
        useParkingStore.getState().setCurrentParkingSession(null);
        removeParkingSessionByEviction(sessionId);
        useUiStore.getState().setSheetModal('');
        break;
      default:
        break;
    }
  }, [handlePrepaidSuccess, paymentState, removeParkingSessionByEviction]);

  const onButtonClick = () => {
    createParking();
  };
  return (
    <>
      <StyledWrapper center {...testIdentifiers('prepaid-info-meta')}>
        <ColumnWithMargins mt={30} mb={20}>
          <Meta number color={theme.colors.gray300}>
            <BaseText color={theme.colors.gray300}>
              {t('services.confirmPermitDescription')}
            </BaseText>
          </Meta>
        </ColumnWithMargins>
      </StyledWrapper>
      <ColumnWithMargins mr={20} ml={20}>
        <StyledWrapper
          mb={20}
          center={isWeb}
          maxWidth={isWeb ? 600 : undefined}>
          <MenuItem
            menuLabel={t('services.location')}
            color={theme.colors.gray300}
            paddingVertical={15}
            testID="prepaid-info-location"
            accessible={false}
            rightComponent={
              <BaseText {...testIdentifiers('prepaid-info-location-text')}>
                {prepaidPurchaseData?.parkzoneLocation}
              </BaseText>
            }
          />
          <MenuItem
            menuLabel={t('services.ticketType')}
            color={theme.colors.gray300}
            paddingVertical={15}
            testID="prepaid-info-type"
            accessible={false}
            rightComponent={
              <BaseText {...testIdentifiers('prepaid-info-type-text')}>
                {prepaidPurchaseData?.prepaidName}
              </BaseText>
            }
          />
          <MenuItem
            menuLabel={t('services.permitVehicle')}
            accessible={false}
            color={theme.colors.gray300}
            paddingVertical={15}
            testID="prepaid-info-vehicle"
            rightComponent={
              <BaseText {...testIdentifiers('prepaid-info-vehicle-text')}>
                {prepaidPurchaseData?.vehicleName
                  ? `${prepaidPurchaseData?.vehicleName} (${prepaidPurchaseData?.licensePlate})`
                  : prepaidPurchaseData?.licensePlate}
              </BaseText>
            }
          />
          {priceText && (
            <MenuItem
              menuLabel={t('price.priceTitle')}
              color={theme.colors.gray300}
              paddingVertical={15}
              testID="prepaid-info-price"
              accessible={false}
              subtitle={serviceFeeText}
              rightComponent={
                <BaseText {...testIdentifiers('prepaid-info-price-text')}>
                  {priceText}
                </BaseText>
              }
            />
          )}
          <MenuItem
            menuLabel={t('services.paymentMethod')}
            accessible={false}
            color={theme.colors.gray300}
            paddingVertical={15}
            testID="prepaid-info-payment"
            rightComponent={
              <CenterVerticalRow gap={5}>
                {walletCard && <Icon name={walletCard.iconName} size="m" />}
                <BaseText {...testIdentifiers('prepaid-info-payment-text')}>
                  {getPaymentMethodDisplayName()}
                </BaseText>
              </CenterVerticalRow>
            }
          />
          <MenuItem
            color={theme.colors.gray300}
            testID="prepaid-info-valid-from"
            menuLabel={t('permit.starts')}
            accessible={false}
            paddingVertical={15}
            rightComponent={
              prepaidPurchaseData?.validFrom ? (
                <Validity
                  date={prepaidPurchaseData.validFrom}
                  testID="prepaid-info-valid-from-text"
                  mode={'datetime'}
                />
              ) : undefined
            }
          />
          <MenuItem
            color={theme.colors.gray300}
            testID="prepaid-info-valid-to"
            menuLabel={t('services.validity')}
            accessible={false}
            paddingVertical={15}
            noBorder
            rightComponent={
              prepaidPurchaseData?.validTo ? (
                <Validity
                  date={prepaidPurchaseData.validTo}
                  testID="prepaid-info-valid-to-text"
                />
              ) : undefined
            }
          />
        </StyledWrapper>
      </ColumnWithMargins>
      <StyledWrapper>
        <Button
          loading={createParkingLoading || isPaymentPending}
          text={t('button.buy')}
          {...testIdentifiers('prepaid-confirm-buy-button')}
          size="l"
          disabled={createParkingLoading || isPaymentPending}
          onPress={onButtonClick}
        />
      </StyledWrapper>
    </>
  );
};

export default PrepaidTicketConfirmModalContent;
